// Constants for the .docx import pipeline (docxToPlate.ts).
//
// mammoth drops underline, w:highlight and w:jc alignment unless told how to
// emit them. These tables drive (a) the styleMap handed to mammoth and (b) the
// DOM post-pass that rewrites the marker classes into the inline styles the
// editor's HTML deserializer already parses (FontBackgroundColorPlugin reads
// inline background-color, TextAlignPlugin reads inline text-align).

// Word's 16 named highlight colors (OOXML ST_HighlightColor) → hex. The hex
// lands as the editor's canonical `backgroundColor` mark — the same mark the
// HighlightColorMenu palette sets (lowercase hex, NOT the boolean `highlight`).
export const DOCX_HIGHLIGHT_HEX: Record<string, string> = {
    yellow: '#ffff00',
    white: '#ffffff',
    green: '#00ff00',
    cyan: '#00ffff',
    magenta: '#ff00ff',
    blue: '#0000ff',
    red: '#ff0000',
    darkBlue: '#00008b',
    darkCyan: '#008b8b',
    darkGreen: '#006400',
    darkMagenta: '#8b008b',
    darkRed: '#8b0000',
    darkYellow: '#808000',
    darkGray: '#a9a9a9',
    lightGray: '#d3d3d3',
    black: '#000000',
};

export const DOCX_HIGHLIGHT_FALLBACK_COLOR = 'yellow';

// w:jc (paragraph justification) → CSS text-align. left/start are the editor
// default and deliberately unmapped.
export const DOCX_JC_TO_TEXT_ALIGN: Record<string, string> = {
    center: 'center',
    right: 'right',
    end: 'right',
    both: 'justify',
};

// Marker classes carried through mammoth's HTML; the post-pass converts them
// to inline styles and strips them so no docx-* class leaks into the document.
export const DOCX_HIGHLIGHT_CLASS_PREFIX = 'docx-hl-';
export const DOCX_ALIGN_CLASS_PREFIX = 'docx-align-';

// Synthetic paragraph style name the alignment transform writes so the
// styleMap can match it (mammoth has no native alignment matcher).
export const DOCX_ALIGN_STYLE_NAME_PREFIX = 'align-';

// Custom styleMap for mammoth.convertToHtml. mammoth consults custom entries
// before its defaults, first match wins — so the color-specific highlight
// matchers must precede the bare `highlight` fallback that catches colors
// outside the named set.
export const DOCX_IMPORT_STYLE_MAP: string[] = [
    'u => u',
    // Word's built-in "Quote" style has no mammoth default mapping (mammoth
    // only ships heading/list/Normal defaults); the editor already registers
    // BlockquotePlugin, so route it to the same HTML tag the editor's
    // deserializer already understands.
    "p[style-name='Quote'] => blockquote:fresh",
    ...Object.keys(DOCX_HIGHLIGHT_HEX).map((color) => `highlight[color='${color}'] => span.${DOCX_HIGHLIGHT_CLASS_PREFIX}${color}`),
    `highlight => span.${DOCX_HIGHLIGHT_CLASS_PREFIX}${DOCX_HIGHLIGHT_FALLBACK_COLOR}`,
    ...Array.from(new Set(Object.values(DOCX_JC_TO_TEXT_ALIGN))).map(
        (align) => `p[style-name='${DOCX_ALIGN_STYLE_NAME_PREFIX}${align}'] => p.${DOCX_ALIGN_CLASS_PREFIX}${align}`,
    ),
];
